// The intake reuses the normal business forms. Track their writes, including
// the final writes fired on tab unmount, so intake submission cannot race them.
type Snapshot = { pending: boolean; error: string };
const listeners = new Set<() => void>();
const pending = new Set<Promise<unknown>>();
const failed = new Map<string, { sequence: number; work: () => Promise<unknown>; message: string }>();
const sequence = new Map<string, number>();
const flushers = new Set<() => Promise<unknown> | undefined>();
export function registerIntakeFactFlusher(flush: () => Promise<unknown> | undefined) {
  flushers.add(flush);
  return () => { flushers.delete(flush); };
}
export async function flushIntakeFactDrafts() {
  await Promise.all(Array.from(flushers, (flush) => flush()));
  await waitForIntakeFactSaves();
}
let snapshot: Snapshot = { pending: false, error: "" };
let active = false;
let session = 0;
export function beginIntakeFactSaveSession() {
  const current = ++session;
  active = true;
  failed.clear();
  emit();
  return () => {
    if (session !== current) return;
    active = false;
    session += 1;
    failed.clear();
    emit();
  };
}
function emit() {
  snapshot = { pending: pending.size > 0, error: Array.from(failed.values()).map((item) => item.message).join(" ") };
  listeners.forEach((listener) => listener());
}
export const subscribeIntakeFactSaves = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export const getIntakeFactSaves = () => snapshot;
export function trackIntakeFactSave<T>(key: string, work: () => Promise<T>): Promise<T> {
  if (!active) return work();
  const currentSession = session;
  const order = (sequence.get(key) ?? 0) + 1;
  sequence.set(key, order);
  const request = Promise.resolve().then(work).then((value) => {
    if (currentSession === session && (failed.get(key)?.sequence ?? 0) <= order) failed.delete(key);
    return value;
  }, (error) => {
    if (currentSession === session && sequence.get(key) === order) failed.set(key, { sequence: order, work, message: error instanceof Error ? error.message : "Some business details did not save." });
    throw error;
  }).finally(() => { pending.delete(request); emit(); });
  pending.add(request);
  emit();
  return request;
}
export async function waitForIntakeFactSaves() {
  while (pending.size) await Promise.allSettled(Array.from(pending));
  if (failed.size) throw new Error(snapshot.error);
}
export async function retryIntakeFactSaves() {
  await Promise.allSettled(Array.from(failed.entries()).map(([key, item]) => trackIntakeFactSave(key, item.work)));
  await waitForIntakeFactSaves();
}
